import { z } from "zod";
import {
  clinicianMeetingSchema,
  MEETING_JOIN_GRACE_MINUTES,
  type ClinicianMeeting,
} from "./meeting";

// TES-1233 (Emelie 2026-10-11, TES-1236 point 1) — "Möten" as a calendar, and
// the protection against a missed meeting. Kirill had already missed a patient
// meeting because the list view gave no overview:
// "Han har redan missat ett patientmöte, så detta är en patientsäkerhets- och arbetsflödesförbättring – inte bara en designförändring."
//
// This file holds the wire shape of the calendar and the derivations every
// surface shares (calendar block, side panel, "Nästa möte", the work-queue
// item), so the calendar and the queue cannot disagree about whether a
// meeting was missed.

// The calendar's range is at most six weeks: the month view's grid.
export const MEETING_CALENDAR_MAX_DAYS = 42;

export const meetingCalendarWindowSchema = z.object({
  doctorId: z.string(),
  startAt: z.string(),
  endAt: z.string(),
});
export type MeetingCalendarWindow = z.infer<typeof meetingCalendarWindowSchema>;

export const meetingCalendarFreeSlotSchema = z.object({
  doctorId: z.string(),
  startAt: z.string(),
  durationMinutes: z.number().int().positive(),
});
export type MeetingCalendarFreeSlot = z.infer<
  typeof meetingCalendarFreeSlotSchema
>;

export const clinicianMeetingCalendarSchema = z.object({
  from: z.string(),
  to: z.string(),
  // Every meeting in the range, cancelled and documented ones included.
  meetings: z.array(clinicianMeetingSchema),
  // The published schedule blocks ("Ledig tid" lives inside them; time
  // outside every window is not bookable).
  windows: z.array(meetingCalendarWindowSchema),
  // Slot-grid starts inside a window that nothing holds, from now on.
  freeSlots: z.array(meetingCalendarFreeSlotSchema),
});
export type ClinicianMeetingCalendar = z.infer<
  typeof clinicianMeetingCalendarSchema
>;

// ---------------------------------------------------------------------------
// Meeting type
// ---------------------------------------------------------------------------

// Emelie: "mötestyp, exempelvis Nybehandling, Receptövertag eller Uppföljning".
// The reason alone cannot say it: a first consult is either a new treatment
// or (after a MEETING_REQUIRED decision) a prescription transfer, and only the
// patient's frozen group tells which.
export type ClinicianMeetingKind =
  | "new_treatment"
  | "prescription_transfer"
  | "follow_up"
  | "renewal_review";

export function clinicianMeetingKind(
  m: Pick<ClinicianMeeting, "reason" | "patientGroup">,
): ClinicianMeetingKind {
  if (m.reason === "follow_up") return "follow_up";
  if (m.reason === "renewal_review") return "renewal_review";
  return m.patientGroup === "PRESCRIPTION_TRANSFER"
    ? "prescription_transfer"
    : "new_treatment";
}

// ---------------------------------------------------------------------------
// Calendar status
// ---------------------------------------------------------------------------

// Emelie's status table, minus "Ledig tid" and "Blockerad/ej bokningsbar tid",
// which are kinds of time rather than states of a meeting.
export const MEETING_CALENDAR_STATUSES = [
  "booked", // Bokat
  "ongoing", // "Pågående"
  "completed", // "Genomfört"
  "cancelled", // Avbokat
  "patient_no_show", // Patient uteblev
  "needs_rebooking", // "Behöver bokas om/läkaren uteblev"
] as const;
export type MeetingCalendarStatus = (typeof MEETING_CALENDAR_STATUSES)[number];

type StatusInput = Pick<
  ClinicianMeeting,
  | "scheduledAt"
  | "durationMinutes"
  | "status"
  | "workflowStatus"
  | "doctorJoinedAt"
  | "patientJoinedAt"
  | "sessionEndedAt"
>;

/** Start + duration + the sweep's grace: when the meeting is over. */
export function meetingClosesAt(
  m: Pick<ClinicianMeeting, "scheduledAt" | "durationMinutes">,
): number {
  return (
    new Date(m.scheduledAt).getTime() +
    (m.durationMinutes + MEETING_JOIN_GRACE_MINUTES) * 60_000
  );
}

/** Over: ended by a verified signal, swept, or past its close. */
export function meetingIsOver(m: StatusInput, now: number): boolean {
  if (m.status === "completed" || m.status === "no_show") return true;
  if (m.sessionEndedAt) return true;
  return now > meetingClosesAt(m);
}

/**
 * Which of Emelie's statuses a meeting is in.
 *
 * After the meeting, who joined decides. `no_show` from the sweep only says
 * that NOBODY took a token, and `completed` from it only that someone did —
 * neither can tell "Patient uteblev" from "läkaren uteblev". The join stamps
 * can: the doctor in and the patient never → the patient stayed away; the
 * doctor never in → the meeting has to be booked again, whatever the patient
 * did. A meeting the doctor documented without joining (held by phone, say)
 * counts as held: he has handled it, and nothing proves otherwise.
 */
export function meetingCalendarStatus(
  m: StatusInput,
  now: number = Date.now(),
): MeetingCalendarStatus {
  if (m.status === "cancelled") return "cancelled";
  if (!meetingIsOver(m, now)) {
    const started =
      m.status === "in_progress" || now >= new Date(m.scheduledAt).getTime();
    return started ? "ongoing" : "booked";
  }
  const doctorIn = Boolean(m.doctorJoinedAt);
  const patientIn = Boolean(m.patientJoinedAt);
  if (doctorIn) return patientIn ? "completed" : "patient_no_show";
  if (m.workflowStatus === "documented") return "completed";
  return "needs_rebooking";
}

/**
 * Emelie: "Varning om mötet har börjat men läkaren inte har öppnat det".
 * From the scheduled start until the meeting is over, while the doctor has
 * not joined.
 */
export function doctorHasNotOpenedStartedMeeting(
  m: StatusInput,
  now: number = Date.now(),
): boolean {
  return (
    meetingCalendarStatus(m, now) === "ongoing" &&
    now >= new Date(m.scheduledAt).getTime() &&
    !m.doctorJoinedAt
  );
}

/**
 * Emelie: "Adminstarten visar alltid Nästa möte". The earliest meeting that
 * is booked or still running — a running one first, so a meeting the doctor
 * has not opened yet does not disappear behind the next booking.
 */
export function nextClinicianMeeting<T extends StatusInput>(
  meetings: readonly T[] | null | undefined,
  now: number = Date.now(),
): T | null {
  let best: T | null = null;
  for (const m of meetings ?? []) {
    const s = meetingCalendarStatus(m, now);
    if (s !== "booked" && s !== "ongoing") continue;
    if (!best || m.scheduledAt.localeCompare(best.scheduledAt) < 0) best = m;
  }
  return best;
}

// ---------------------------------------------------------------------------
// The work-queue item for a passed meeting
// ---------------------------------------------------------------------------

// Emelie: "Ett passerat möte utan dokumentation eller aktiv hantering skapar en operativ uppgift i arbetskön."
//
// "Aktiv hantering" = a clinician wrote in the meeting's journal recently. A
// draft untouched for longer than this is no longer being handled, and the
// item comes back.
export const MEETING_ACTIVE_HANDLING_HOURS = 24;
// How far back the lane looks; the same window as the doctor's meeting list.
export const MEETING_TO_DOCUMENT_LOOKBACK_DAYS = 30;

// ---------------------------------------------------------------------------
// Swedish labels — Emelie's words from TES-1236 point 1, verbatim
// ---------------------------------------------------------------------------

export const MEETING_KIND_LABEL: Record<ClinicianMeetingKind, string> = {
  new_treatment: "Nybehandling",
  prescription_transfer: "Receptövertag",
  follow_up: "Uppföljning",
  // Not in Emelie's list; the label the portal already uses (meeting-labels).
  renewal_review: "Receptförnyelse",
};

export const MEETING_CALENDAR_STATUS_LABEL: Record<
  MeetingCalendarStatus,
  string
> = {
  booked: "Bokat",
  ongoing: "Pågående",
  completed: "Genomfört",
  cancelled: "Avbokat",
  patient_no_show: "Patient uteblev",
  needs_rebooking: "Behöver bokas om/läkaren uteblev",
};

// The work-queue item's title, by what happened: the three categories of
// "Passerade möten", verbatim from A-02's canonical addendum 2026-10-13
// (TES-1404).
export const MEETING_TO_DOCUMENT_TITLE: Partial<
  Record<MeetingCalendarStatus, string>
> = {
  completed: "Genomfört – dokumentation saknas",
  patient_no_show: "Patienten anslöt inte",
  needs_rebooking: "Läkaren anslöt inte eller mötet avbröts tekniskt",
};
